import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { createOtpProviderColumns } from './constants'
import useMutationDeleteOtpProvider from '@/modules/otp-provider/api/hooks/useMutationDeleteOtpProvider'
import ConfirmDialog from '@/common/components/ConfirmDialog'
import type { components } from '@/schema'

type OtpProvider = components['schemas']['AdminOtpProviderResponse']

interface OtpProviderTableProps {
	data: OtpProvider[]
	isLoading: boolean
}

export default function OtpProviderTable({
	data,
	isLoading,
}: OtpProviderTableProps) {
	const navigate = useNavigate()
	const deleteMutation = useMutationDeleteOtpProvider()
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [selectedProviderId, setSelectedProviderId] = useState<string>('')

	const handleEdit = (id: string) => {
		navigate(`/operator/otp-provider/${id}/edit`)
	}

	const handleDelete = (id: string) => {
		setSelectedProviderId(id)
		setDeleteDialogOpen(true)
	}

	const handleConfirmDelete = async () => {
		if (selectedProviderId) {
			await deleteMutation.mutateAsync(selectedProviderId)
			setDeleteDialogOpen(false)
			setSelectedProviderId('')
		}
	}

	const columns = createOtpProviderColumns(handleEdit, handleDelete)

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
	})

	if (isLoading) {
		return (
			<div className="flex h-32 items-center justify-center">
				<div className="text-muted-foreground">กำลังโหลด...</div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className="flex h-32 items-center justify-center">
				<div className="text-muted-foreground">ไม่พบข้อมูลผู้ให้บริการ OTP</div>
			</div>
		)
	}

	return (
		<>
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows.map((row) => (
							<TableRow key={row.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			<ConfirmDialog
				open={deleteDialogOpen}
				onOpenChange={setDeleteDialogOpen}
				onConfirm={handleConfirmDelete}
				title="ยืนยันการลบผู้ให้บริการ OTP"
				description="คุณแน่ใจหรือไม่ว่าต้องการลบผู้ให้บริการ OTP นี้? การดำเนินการนี้ไม่สามารถยกเลิกได้"
				confirmText="ลบ"
				cancelText="ยกเลิก"
				isDestructive
			/>
		</>
	)
}
